import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authClient, useSession } from '../lib/auth-client';
import { Button, Card, CardBody, Input } from '@img/ui';
import { RamhCredit } from '../components/RamhCredit';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: session, isPending } = useSession();
  // 🔁 وجهة بعد الدخول: ?redirect=... أو من sessionStorage (مثلاً رابط دعوة جمعية)
  const redirectTarget = (() => {
    const fromQuery = searchParams.get('redirect');
    if (fromQuery && fromQuery.startsWith('/')) return fromQuery;
    try {
      const stored = sessionStorage.getItem('postLoginRedirect');
      if (stored && stored.startsWith('/')) return stored;
    } catch { /* ignore */ }
    return '/dashboard';
  })();

  const [email, setEmail] = useState('admin@img.sa');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0); // ⏱️ ثواني المؤقّت لإعادة الإرسال

  // ⏱️ مؤقّت العد التنازلي
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (isPending) return null;
  if (session?.user) {
    try { sessionStorage.removeItem('postLoginRedirect'); } catch { /* ignore */ }
    return <Navigate to={redirectTarget} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNeedsVerification(false);
    setResendMsg(null);
    try {
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) {
        const msg = signInError.message ?? '';
        // 🔍 نتعرّف على خطأ "غير مفعّل"
        const isEmailNotVerified =
          (signInError as { code?: string }).code === 'EMAIL_NOT_VERIFIED' ||
          /not.?verified|verify your email|email.*not.*verif/i.test(msg);
        if (isEmailNotVerified) {
          setNeedsVerification(true);
          setError('لم يتم تفعيل حسابك بعد. تحقق من بريدك أو أعد إرسال رسالة التفعيل.');
        } else {
          setError(msg || 'فشل تسجيل الدخول');
        }
      } else {
        try { sessionStorage.removeItem('postLoginRedirect'); } catch { /* ignore */ }
        navigate(redirectTarget);
      }
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message ?? 'فشل تسجيل الدخول');
    } finally {
      setBusy(false);
    }
  };

  const handleResendVerification = async () => {
    if (cooldown > 0) return;
    if (!email) {
      setResendMsg('اكتب البريد الإلكتروني أولاً');
      return;
    }
    setResendBusy(true);
    setResendMsg(null);
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/login?verified=1`,
      });
      setResendMsg('✅ تم إرسال رسالة التفعيل، تحقق من بريدك (والـ Spam).');
      setCooldown(60); // ⏱️ ٦٠ ثانية قفل
    } catch {
      setResendMsg('⚠️ تعذّر الإرسال — تأكد من البريد ثم حاول مرة ثانية.');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--bg-light)]">
      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block bg-navy-dark text-white px-4 py-2 rounded-lg font-black mb-3">
              {t('app.name')}
            </Link>
            <h1 className="text-2xl font-black text-navy-dark">{t('nav.login')}</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="البريد الإلكتروني"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="كلمة المرور"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
            {needsVerification && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="text-amber-800 text-sm font-bold">
                  ✉️ حسابك لم يُفعَّل بعد
                </div>
                <p className="text-amber-700 text-xs">
                  أرسلنا لك رابط تفعيل عند التسجيل. لو ما وصلك، اضغط الزر أدناه لإعادة الإرسال.
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResendVerification}
                  loading={resendBusy}
                  disabled={cooldown > 0}
                  className="w-full"
                >
                  {cooldown > 0
                    ? `⏱️ يمكنك إعادة الإرسال خلال ${cooldown} ثانية`
                    : '📨 إعادة إرسال رسالة التفعيل'}
                </Button>
                {resendMsg && (
                  <div className={`text-xs ${resendMsg.startsWith('✅') ? 'text-emerald-700' : 'text-red-700'}`}>
                    {resendMsg}
                  </div>
                )}
              </div>
            )}
            <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
              {t('nav.login')}
            </Button>
          </form>

          <div className="text-center text-sm space-y-2 mt-6">
            <div>
              <Link to="/forgot-password" className="text-gray-500 hover:text-navy-dark font-bold">
                نسيت كلمة المرور؟
              </Link>
            </div>
            <div className="text-gray-600">
              ما عندك حساب؟{' '}
              <Link to="/register" className="text-navy-dark font-bold hover:text-gold">
                {t('nav.register')}
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>
      <div className="mt-4"><RamhCredit /></div>
    </div>
  );
}
